import { SCORE_STARS, type GiftKind, type StarFactor } from '@accra/shared';
import { BoxGeometry, DoubleSide, ExtrudeGeometry, Group, Mesh, MeshStandardMaterial, PlaneGeometry, Shape } from 'three';
import type { Pickup } from '../../game/RunState';
import { canvasTexture } from '../../render/materials/canvasTexture';

const MAX_EACH = 4;

function starShape(outer: number, inner: number): Shape {
  const shape = new Shape();
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? outer : inner;
    const a = Math.PI / 2 + (i * Math.PI) / 5;
    if (i === 0) shape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else shape.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  shape.closePath();
  return shape;
}

function label(text: string, color: string): MeshStandardMaterial {
  const map = canvasTexture(128, 64, (ctx) => {
    ctx.font = '900 52px Impact, "Arial Black", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#1b1b1b';
    ctx.strokeText(text, 64, 34);
    ctx.fillStyle = color;
    ctx.fillText(text, 64, 34);
  });
  return new MeshStandardMaterial({ map, transparent: true, emissive: '#ffffff', emissiveMap: map, emissiveIntensity: 0.6, side: DoubleSide, depthWrite: false });
}

/** A wrapped present (red, gold ribbon) or a fitter's toolbox (blue, spanner). */
function giftMaterial(kind: GiftKind): MeshStandardMaterial {
  return new MeshStandardMaterial({
    roughness: 0.6,
    map: canvasTexture(128, 128, (ctx) => {
      ctx.fillStyle = kind === 'cash' ? '#ce1126' : '#1d4ed8';
      ctx.fillRect(0, 0, 128, 128);
      ctx.fillStyle = kind === 'cash' ? '#fcd116' : '#e5e7eb';
      if (kind === 'cash') {
        ctx.fillRect(54, 0, 20, 128);
        ctx.fillRect(0, 54, 128, 20);
      } else {
        ctx.font = '76px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('🔧', 64, 70);
      }
    }),
  });
}

interface StarSlot {
  group: Group;
  body: Mesh;
  tag: Mesh;
}

/** Score stars (spinning, with a ×N tag) and gifts (bobbing boxes). A few of each at a time. */
export class BonusPickupsView {
  readonly group = new Group();
  private readonly stars: StarSlot[] = [];
  private readonly gifts: Mesh[] = [];
  private readonly starLooks = new Map<StarFactor, { body: MeshStandardMaterial; tag: MeshStandardMaterial }>();
  private readonly giftLooks = new Map<GiftKind, MeshStandardMaterial>();
  private clock = 0;

  constructor() {
    const starGeometry = new ExtrudeGeometry(starShape(0.7, 0.3), { depth: 0.16, bevelEnabled: false }).center();
    const tagGeometry = new PlaneGeometry(1.2, 0.6);
    for (const s of SCORE_STARS) {
      const body = new MeshStandardMaterial({ color: s.color, emissive: s.color, emissiveIntensity: 0.45, metalness: 0.5, roughness: 0.3 });
      this.starLooks.set(s.factor, { body, tag: label(`×${s.factor}`, s.color) });
    }
    const first = this.starLooks.get(2);
    for (let i = 0; i < MAX_EACH && first; i += 1) {
      const group = new Group();
      const body = new Mesh(starGeometry, first.body);
      body.castShadow = true;
      const tag = new Mesh(tagGeometry, first.tag);
      tag.position.y = 1.05;
      group.add(body, tag);
      group.visible = false;
      this.stars.push({ group, body, tag });
      this.group.add(group);
    }
    this.giftLooks.set('cash', giftMaterial('cash'));
    this.giftLooks.set('fitter', giftMaterial('fitter'));
    const box = new BoxGeometry(0.9, 0.7, 0.9);
    for (let i = 0; i < MAX_EACH; i += 1) {
      const gift = new Mesh(box, this.giftLooks.get('cash'));
      gift.castShadow = true;
      gift.visible = false;
      this.gifts.push(gift);
      this.group.add(gift);
    }
  }

  sync(pickups: readonly Pickup[], dt: number): void {
    this.clock += dt;
    let starIndex = 0;
    let giftIndex = 0;
    for (const p of pickups) {
      if (p.kind === 'star' && p.factor && starIndex < MAX_EACH) {
        const slot = this.stars[starIndex++];
        const look = this.starLooks.get(p.factor);
        if (!slot || !look) continue;
        slot.group.visible = true;
        slot.body.material = look.body;
        slot.tag.material = look.tag;
        slot.group.position.set(p.x, p.y + Math.sin(this.clock * 3 + p.s) * 0.15, -p.s);
        slot.body.rotation.y = this.clock * 3;
      } else if (p.kind === 'gift' && p.gift && giftIndex < MAX_EACH) {
        const gift = this.gifts[giftIndex++];
        if (!gift) continue;
        gift.visible = true;
        gift.material = this.giftLooks.get(p.gift) ?? gift.material;
        gift.position.set(p.x, p.y + Math.abs(Math.sin(this.clock * 4 + p.s)) * 0.25, -p.s);
        gift.rotation.y = this.clock * 1.2;
      }
    }
    for (let i = starIndex; i < MAX_EACH; i += 1) {
      const slot = this.stars[i];
      if (slot) slot.group.visible = false;
    }
    for (let i = giftIndex; i < MAX_EACH; i += 1) {
      const gift = this.gifts[i];
      if (gift) gift.visible = false;
    }
  }
}
